import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { CustomSnackBarService } from '@app/shared/components/custom-snack-bar/custom-snack-bar.service';
import { SimpleFormDialogComponent } from '@app/shared/custom-dialogs/simple-form-dialog/simple-form-dialog.component';
import { SimpleFormModel } from '@app/shared/models/simple-form.model';
import { EventEmitedModel, TableMenuModel, TableStructureModel } from '@app/shared/models/table.model';
// Importar el servicio de productos según tu estructura
import { ProductsService } from '@app/shared/services/entities/products.service';


import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '@app/material.module';
import { CustomTableComponent } from '@shared/components/custom-table/custom-table.component';
@Component({
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    CustomTableComponent
],
  selector: 'app-procedure-requirements',
  templateUrl: './procedure-requirements.component.html',
  styleUrls: ['./procedure-requirements.component.scss']
})
export class ProcedureRequirementsComponent implements OnInit {

  public structure: TableStructureModel[] = [
    {columnKey: "product", columnName: "Insumo", type: "string"},
    {columnKey: "itemNumber", columnName: "Código", type: "string"},
    {columnKey: "quantity", columnName: "Cantidad", type: "number"},
    {columnKey: "unit", columnName: "Unidad", type: "string"},
  ];

  public menu: TableMenuModel[] = [
    {icon: "edit", action: "update", color: "primary", display:"Editar cantidad"},
    {icon: "delete", action: "delete", color: "warn", display:"Quitar"}
  ];

  /** `app-custom-table` (shared) recibe estructura y menú dentro de `config`. */
  public get tableConfig() {
    return { structure: this.structure, menu: this.menu };
  }

  public requirements: any[] = [];
  public availableProducts: any[] = [];
  public procedureData: any;
  public contractData: any;

  public inputsToAdd: SimpleFormModel[] = [
    {
      name: "product",
      displayName: "Insumo",
      type: "select",
      options: [],
      required: true
    },
    {
      name: "quantity",
      displayName: "Cantidad",
      type: "number",
      required: true
    }
  ];

  public inputsToUpdate: SimpleFormModel[] = [
    {
      name: "quantity",
      displayName: "Cantidad",
      type: "number",
      required: true
    }
  ];

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<ProcedureRequirementsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private customMessage: CustomMessageService,
    private customSnackBar: CustomSnackBarService,
    private productService: ProductsService, // Descomentar cuando tengas el servicio
  ) { }

  async ngOnInit() {
    this.procedureData = { ...this.data.procedure };
    this.contractData = { ...this.data.contract };
    
    // Cargar requerimientos existentes
    if (this.procedureData.requirements && this.procedureData.requirements.length > 0) {
      this.requirements = [...this.procedureData.requirements];
    }

    await this.loadAvailableProducts();
  }

  /**@description Cargar productos disponibles */
  async loadAvailableProducts() {
    try {
      this.customMessage.open({
        title: "Espere", 
        message: "Cargando insumos disponibles", 
        type: "neutral", 
        loading: true
      });

      const apiResponse: any = await this.productService.search({
        searchType: 'default',
        conditions: { deleted: false, active: true }
      });


      this.customMessage.close();

      if (apiResponse.status === "success") {
        this.availableProducts = apiResponse.data;
        
        // Actualizar opciones del select
        let input = this.inputsToAdd.find(x => x.name == "product");
        if (input) {
          input.options = this.availableProducts.map(p => ({
            id: p.id,
            name: `${p.name} - ${p.code}`,
            code: p.code,
            unit: p.unit
          }));
        }
      } else {
        this.customSnackBar.open("Error al cargar insumos", "info");
      }
    } catch (err) {
      this.customMessage.close();
      this.customMessage.open({
        title: "Error", 
        message: `Ocurrió un error al cargar insumos. ${err}`, 
        type: "error", 
        dismissBtn: true
      });
    }
  }

  /**@description Abrir formulario para agregar producto */
  async openAddProductForm() {
    try {
      const dialogRef = this.dialog.open(SimpleFormDialogComponent, {
        disableClose: true,
        data: {
          title: "Agregar Insumo Requerido",
          type: "create",
          item: {},
          inputs: this.inputsToAdd,
          action: this.addRequirement.bind(this)
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        // Ya se procesó en addRequirement
      });
    } catch (err) {
      this.customMessage.open({
        title: "Error", 
        message: `Ocurrió un error inesperado. ${err}`, 
        type: "error", 
        dismissBtn: true
      });
    }
  }

  /**@description Agregar producto a requerimientos */
  async addRequirement(item: any) {
    try {
      const product = this.availableProducts.find(p => p.id === item.product);
      
      if (!product) {
        this.customSnackBar.open("Insumo no encontrado", "info");
        return;
      }

      // Verificar si ya existe
      const exists = this.requirements.some(r => r.product_id === product.id);
      if (exists) {
        this.customSnackBar.open("Este insumo ya está agregado", "info");
        return;
      }

      // Agregar a la lista
      this.requirements.push({
        product_id: product.id,
        product_name: product.name,
        product_code: product.code,
        quantity: item.quantity,
        unit: product.unit
      });

      this.customSnackBar.open("Insumo agregado", "success");
      return { status: "success" };
    } catch (err) {
      this.customMessage.open({
        title: "Error", 
        message: `Ocurrió un error inesperado. ${err}`, 
        type: "error", 
        dismissBtn: true
      });
    }
  }

  /**@description Abrir formulario para editar cantidad */
  async openUpdateQuantityForm(item: any) {
    try {
      const dialogRef = this.dialog.open(SimpleFormDialogComponent, {
        disableClose: true,
        data: {
          title: "Actualizar Cantidad",
          type: "update",
          item: { quantity: item.quantity },
          inputs: this.inputsToUpdate,
          action: (id: number, data: any) => this.updateRequirement(item, data)
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        // Ya se procesó en updateRequirement
      });
    } catch (err) {
      this.customMessage.open({
        title: "Error", 
        message: `Ocurrió un error inesperado. ${err}`, 
        type: "error", 
        dismissBtn: true
      });
    }
  }

  /**@description Actualizar cantidad de producto */
  async updateRequirement(item: any, data: any) {
    try {
      const index = this.requirements.findIndex(r => r.product_id === item.product_id);
      if (index !== -1) {
        this.requirements[index].quantity = data.quantity;
        this.customSnackBar.open("Cantidad actualizada", "success");
        return { status: "success" };
      }
    } catch (err) {
      this.customMessage.open({
        title: "Error", 
        message: `Ocurrió un error inesperado. ${err}`, 
        type: "error", 
        dismissBtn: true
      });
    }
  }

  /**@description Quitar producto de requerimientos */
  async removeRequirement(item: any) {
    try {
      let response = await this.customMessage.open({
        title: "Cuidado", 
        type: "warning", 
        message: `¿Está seguro de quitar este insumo?`, 
        dismissBtn: true,
        acceptBtn: true,
        acceptBtnText: "Continuar"
      });

      if (!response) return;

      const index = this.requirements.findIndex(r => r.product_id === item.product_id);
      if (index !== -1) {
        this.requirements.splice(index, 1);
        this.customSnackBar.open("Insumo quitado", "success");
      }
    } catch (err) {
      this.customMessage.open({
        title: "Error", 
        message: `Ocurrió un error inesperado. ${err}`, 
        type: "error", 
        dismissBtn: true
      });
    }
  }

  /**@description Manejar acciones de la tabla */
  action(response: EventEmitedModel) {
    if (response.action == "update") this.openUpdateQuantityForm(response.item);
    if (response.action == "delete") this.removeRequirement(response.item);
  }

  /**@description Guardar requerimientos y cerrar */
  save() {
    this.dialogRef.close({
      requirements: this.requirements
    });
  }

  /**@description Cerrar sin guardar */
  close() {
    this.dialogRef.close(null);
  }
}